import { ref } from "vue";
import pososapi from "@/api/pososapi";

/**
 * Composable for managing POSOS widget authentication tokens.
 * User identity and region are derived server-side from the
 * authenticated session and POSOS config.
 *
 * Usage:
 *   const { fetchToken, token } = usePososAuth();
 *   const t = await fetchToken();
 *   widget.onAuthTokenRefresh(fetchToken);
 */



// Module-level state — shared singleton across all consumers.
// One token cache for the whole app, not per-component.
const token = ref<string | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);

let expiresAt = 0;
let inFlight: Promise<string> | null = null;

export function usePososAuth() {
  async function fetchToken(force = false): Promise<string> {
    // 1. Reuse a still-valid token (30s safety margin).
    if (!force && token.value && Date.now() < expiresAt - 30000) {
      return token.value;
    }
    // 2. Reuse an in-flight request instead of starting a second.
    if (inFlight) return inFlight;
    loading.value = true;
    error.value = null;
    inFlight = (async () => {
      try {
        const response = await pososapi.getWidgetToken();
        if (!response.token) throw new Error("POSOS returned empty token");
        token.value = response.token;
        // Default to a safe value (assumes IAP tokens are ~55 min)
        expiresAt = Date.now() + (response.expiresIn ?? 3300) * 1000;
        return response.token;
      } catch (err: any) {
        error.value = err?.message || "Failed to fetch POSOS widget token";
        throw err;
      } finally {
        loading.value = false;
        inFlight = null;
      }
    })();
    return inFlight;
  }

  return {
    token,
    loading,
    error,
    fetchToken,
  };
}